/**
 * JobLedgerPanel.jsx — Per-job earnings ledger view (admin/payroll-full).
 *
 * Shows persisted job_earnings_ledger rows for a single job, grouped by state:
 *   - Pending  (computed but trigger not fired)
 *   - Earned   (trigger fired, awaiting payment)
 *   - Paid     (paid out)
 *   - Forfeited
 *
 * Super-admin sees an "Un-earn" button on Earned rows.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtC } from '../../lib/utils';
import { api } from '../../lib/api';

const STATE_CFG = {
  pending:   { label: 'Pending',   tone: 'text-text-dim',    desc: 'Computed but trigger has not fired.' },
  earned:    { label: 'Earned',    tone: 'text-accent3',     desc: 'Trigger fired — awaiting payout.' },
  paid:      { label: 'Paid',      tone: 'text-green',       desc: 'Paid out.' },
  forfeited: { label: 'Forfeited', tone: 'text-red-accent/70', desc: 'Job voided or otherwise canceled.' },
};

const ROLE_LABEL = {
  originator:  'Originator',
  prod_lead:   'Production Lead',
  prod_hourly: 'Production',
  post_lead:   'Post Lead',
  post_hourly: 'Post',
};

export default function JobLedgerPanel({ jobId }) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['job-earnings-ledger', jobId],
    queryFn: () => api.get(`/job-earnings-ledger?job_id=${jobId}`),
    enabled: !!jobId,
  });

  const grouped = useMemo(() => {
    const g = { pending: [], earned: [], paid: [], forfeited: [] };
    rows.forEach(r => { if (g[r.state]) g[r.state].push(r); });
    Object.values(g).forEach(arr => arr.sort((a, b) => (b.amount || 0) - (a.amount || 0)));
    return g;
  }, [rows]);

  if (isLoading) return <div className="text-2xs text-text-dim">Loading ledger…</div>;
  if (rows.length === 0) {
    return <div className="text-2xs text-text-dim">No ledger rows yet — recompute on next status/time-entry change.</div>;
  }

  return (
    <div className="space-y-3 text-xs">
      {Object.entries(STATE_CFG).map(([state, cfg]) => {
        const list = grouped[state];
        if (list.length === 0) return null;
        const total = list.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        return (
          <div key={state}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <span className={`font-mono uppercase tracking-wider text-2xs ${cfg.tone}`}>{cfg.label}</span>
                <span className="text-text-dim text-2xs ml-2">{cfg.desc}</span>
              </div>
              <span className={`font-mono ${cfg.tone}`}>{fmtC(total)}</span>
            </div>
            <div className="space-y-0.5">
              {list.map(r => (
                <div key={r.id} className="flex items-center justify-between py-1 px-2 bg-surface-2/50 rounded">
                  <div className="min-w-0 flex-1">
                    <span className="text-text-primary">{r.person}</span>
                    <span className="text-text-dim ml-2">{ROLE_LABEL[r.role] || r.role}</span>
                    {r.trigger_fired_at && state !== 'pending' && (
                      <span className="text-text-dim text-2xs ml-2">fired {new Date(r.trigger_fired_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="font-mono text-text-primary flex-shrink-0">{fmtC(r.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
